import { Button, Flex, FlexProps, Popover, Text, TextField } from 'components'
import { GlobeAltIcon } from 'components/icons/hero-icons'
import { useWorkspace } from 'components/providers'
import { isEmpty } from 'lodash-es'
import { useState } from 'react'
import {
  ILayout,
  ITrait,
  TemplateRoute,
  TemplateRouteNode,
  TemplateRouting,
  TraitWrapper,
} from 'types'
import { attachRoutingChild, updateRouting } from '../../../utils'
import { getNamedLayoutMap, makeEmptyTrait, useTraits } from '../../../utils/traitUtils'
import { useTemplate } from '../TemplateProvider'

export interface NewRouteProps {
  onEditTrait: (traitKey: number) => void
  onUpdate: (newRoute: TemplateRoute) => void
  route?: TemplateRoute
  routings: TemplateRouting[]
  selectedIds: string[]
  traitMap?: Record<number, ITrait>
}

export default function NewRoute({
  onEditTrait,
  onUpdate,
  route = [],
  routings,
  selectedIds,
  traitMap = {},
}: NewRouteProps) {
  const { template, updateTemplate } = useTemplate()
  const { workspace } = useWorkspace()
  const { traits } = useTraits()

  const [layoutPopoverOpen, setLayoutPopoverOpen] = useState(false)
  const [newLayoutLabel, setNewLayoutLabel] = useState('')

  if (!template) return null
  if (!isEmpty(template.routing) && selectedIds.length === 0) return null

  const lastRouting = routings[routings.length - 1]

  const usedTraitIds = new Set(
    Object.values(template.traitMap ?? {})
      .map(wrapper => wrapper.id)
      .filter((id): id is string => Boolean(id))
  )

  const workspaceTraitsNotOnTemplate = traits.filter(trait => !usedTraitIds.has(trait.id))

  const namedLayoutMap = template.layoutMap ? getNamedLayoutMap(template.layoutMap) : {}
  const usedTraitKeys = new Set(
    routings.filter(routing => routing.type === 'trait').map(routing => routing.key)
  )
  const existingRouteTraits = Object.entries(traitMap).filter(
    ([key]) => !usedTraitKeys.has(Number(key))
  )
  const canAddRouteNode = selectedIds.length > 0

  function getNextTraitKey(traitWrappers?: Record<number, TraitWrapper>) {
    const existingKeys = Object.keys(traitWrappers ?? {}).map(Number)
    return existingKeys.length > 0 ? Math.max(...existingKeys) + 1 : 1
  }

  function getNextLayoutKey(layoutMap?: Record<number, ILayout>) {
    const existingKeys = Object.keys(layoutMap ?? {}).map(Number)
    return existingKeys.length > 0 ? Math.max(...existingKeys) + 1 : 1
  }

  function handleAddTrait(traitId?: string) {
    if (!template || !workspace) return

    const selectedTrait = traits.find(trait => trait.id === traitId)
    const existingTraitMap = template.traitMap ?? {}
    const nextKey = getNextTraitKey(existingTraitMap)

    const updatedTraitMap: Record<number, TraitWrapper> = {
      ...existingTraitMap,
      [nextKey]: selectedTrait ? { id: selectedTrait.id } : makeEmptyTrait(workspace.id),
    }
    const nextNode: TemplateRouteNode = { key: nextKey, type: 'trait' }
    const traitMapForRouting: Record<number, ITrait> = {
      ...traitMap,
      [nextKey]: (selectedTrait ?? makeEmptyTrait(workspace.id)) as ITrait,
    }

    if (isEmpty(template.routing) || routings.length === 0) {
      const routing = updateRouting(
        template.routing ?? ({} as TemplateRouting),
        route,
        nextNode,
        traitMapForRouting
      )
      updateTemplate({ routing, traitMap: updatedTraitMap })
      if (!traitId) onEditTrait(nextKey)
      return
    }

    if (!attachChild(nextNode, traitMapForRouting, updatedTraitMap)) {
      updateTemplate({ traitMap: updatedTraitMap })
    }
    if (!traitId) onEditTrait(nextKey)
  }

  function handleRoute(nextRouting: TemplateRouteNode) {
    if (!template) return

    if (routings.length === 0) {
      setRootNode(nextRouting)
    } else {
      attachChild(nextRouting)
    }
  }

  function setRootNode(nextNode: TemplateRouteNode, traitMapForRouting?: Record<number, ITrait>) {
    const routing = updateRouting(
      template!.routing ?? ({} as TemplateRouting),
      route,
      nextNode,
      traitMapForRouting ?? traitMap
    )
    if (!routing) return

    updateTemplate({ routing })
    onUpdate(route)
  }

  function attachChild(
    nextNode: TemplateRouteNode,
    traitMapForRouting?: Record<number, ITrait>,
    traitMapUpdate?: Record<number, TraitWrapper>
  ) {
    if (!template?.routing || !canAddRouteNode || !lastRouting) return false

    const result = attachRoutingChild(
      template.routing,
      route,
      selectedIds,
      nextNode,
      traitMapForRouting ?? traitMap
    )
    if (!result) return false

    updateTemplate({
      routing: result.routing,
      ...(traitMapUpdate && { traitMap: traitMapUpdate }),
    })
    onUpdate(result.route)
    return true
  }

  function handleCreateLayout() {
    if (!template) return

    const label = newLayoutLabel.trim()
    if (!label) return

    const existingLayoutMap = template.layoutMap ?? {}
    const nextKey = getNextLayoutKey(existingLayoutMap)

    const newLayout: ILayout = {
      type: 'flex',
      direction: 'column',
      layouts: [],
      label,
    }

    const updatedLayoutMap: Record<number, ILayout> = {
      ...existingLayoutMap,
      [nextKey]: newLayout,
    }

    if (routings.length === 0) {
      const routing = updateRouting(
        template.routing ?? ({} as TemplateRouting),
        route ?? [],
        { key: nextKey, type: 'layout' },
        traitMap
      )
      if (routing === undefined) {
        updateTemplate({ layoutMap: updatedLayoutMap })
      } else {
        updateTemplate({ layoutMap: updatedLayoutMap, routing })
      }
      onUpdate(route ?? [])
    } else {
      updateTemplate({ layoutMap: updatedLayoutMap })
      handleRoute({ type: 'layout', key: nextKey })
    }

    setNewLayoutLabel('')
    setLayoutPopoverOpen(false)
  }

  return (
    <Flex
      direction="column"
      border="1px dashed"
      bg="rgba(255, 255, 255, 0.54)"
      borderColor="gray.200"
      borderRadius="md"
      p={4}
      gap={8}
      overflow="auto"
    >
      <Flex direction="column" gap={6}>
        {existingRouteTraits.length > 0 && (
          <Flex direction="column" gap={2}>
            <Text color="gray.700" fontSize="sm" fontWeight="semibold">
              Existing Traits
            </Text>
            <Flex direction="column">
              {existingRouteTraits.map(([key, trait]) => (
                <NewItemButton
                  key={key}
                  onClick={
                    canAddRouteNode
                      ? () => handleRoute({ key: Number(key), type: 'trait' })
                      : undefined
                  }
                >
                  {trait.title.en}
                </NewItemButton>
              ))}
            </Flex>
          </Flex>
        )}

        <Flex direction="column" gap={2}>
          <Text color="gray.700" fontSize="sm" fontWeight="semibold">
            Workspace Traits
          </Text>
          <Flex direction="column">
            {workspaceTraitsNotOnTemplate.map(trait => (
              <NewItemButton key={trait.id} onClick={() => handleAddTrait(trait.id)}>
                <Flex alignItems="center" gap={2}>
                  <GlobeAltIcon boxSize={4} color="primary.500" type="outline" />
                  <Text fontSize="sm">{trait.title.en}</Text>
                </Flex>
              </NewItemButton>
            ))}

            <NewItemButton onClick={() => handleAddTrait()}>+ New Trait</NewItemButton>
          </Flex>
        </Flex>
      </Flex>

      <Flex direction="column">
        <Text color="gray.700" fontSize="sm" fontWeight="semibold">
          Layouts
        </Text>

        <Flex direction="column">
          {Object.entries(namedLayoutMap).map(([key, layout]) => (
            <NewItemButton
              key={key}
              onClick={
                routings.length === 0 || canAddRouteNode
                  ? () => handleRoute({ key: Number(key), type: 'layout' })
                  : undefined
              }
            >
              {layout.label}
            </NewItemButton>
          ))}

          <Popover
            header="New Layout"
            trigger={
              <NewItemButton onClick={() => setLayoutPopoverOpen(true)}>+ New Layout</NewItemButton>
            }
            open={layoutPopoverOpen}
            onOpenChange={p => setLayoutPopoverOpen(p.open)}
            portal
            positioning={{ placement: 'bottom-start' }}
          >
            <Flex direction="column" gap={3} minWidth="260px" p={4}>
              <TextField
                autoFocus
                label="Layout name"
                value={newLayoutLabel}
                onChange={event => setNewLayoutLabel(event.target.value)}
              />

              <Flex gap={2} justifyContent="flex-end">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setLayoutPopoverOpen(false)
                    setNewLayoutLabel('')
                  }}
                >
                  Cancel
                </Button>
                <Button
                  disabled={!newLayoutLabel.trim()}
                  palette="primary"
                  onClick={handleCreateLayout}
                >
                  Done
                </Button>
              </Flex>
            </Flex>
          </Popover>
        </Flex>
      </Flex>
    </Flex>
  )
}

function NewItemButton(props: FlexProps) {
  return (
    <Flex
      px={4}
      py={3}
      cursor={props.onClick ? 'pointer' : 'default'}
      _hover={props.onClick ? { bg: 'gray.50' } : undefined}
      {...props}
    />
  )
}
